// Centralized Easter Egg Configuration
// This file contains all easter egg definitions, colors, and settings in one place

import React from 'react'
import { audioEffects } from '../utils/audioEffects'

export interface EasterEggConfig {
  id: string
  name: string
  description: string
  unlockedColor: {
    name: string
    primary: string
    secondary: string
  }
}

// All easter eggs are defined here - add, edit, or remove easter eggs in this array
export const EASTER_EGG_CONFIGS: EasterEggConfig[] = [
    {
        id: 'village_people',
        name: 'De Village People',
        description: 'Born To Be Alive is wel van de Village People.',
        unlockedColor: {
            name: 'Disco',
            primary: '#7c35e7',
            secondary: '#ffb814'
        }
    },
    {
        id: 'custom_message',
        name: 'Lief <3 (Hopelijk)',
        description: 'Kies je eigen koosnaampje voor mij.',
        unlockedColor: {
            name: 'Vaporwave',
            primary: '#ff71ce',
            secondary: '#01cdfe'
        }
    },
    {
        id: 'avatar_mode',
        name: 'Kickflip!',
        description: 'Kickflip de profielfoto.',
        unlockedColor: {
            name: 'Avatar',
            primary: '#00ffff',
            secondary: '#ff00ff'
        }
    }
]

// Automatically generate EASTER_EGGS object from configs for backward compatibility
export const EASTER_EGGS = EASTER_EGG_CONFIGS.reduce((acc, config) => {
  acc[config.id.toUpperCase()] = {
    id: config.id,
    name: config.name,
    description: config.description
  }
  return acc
}, {} as Record<string, { id: string; name: string; description: string }>)

// Get easter egg config by ID
export const getEasterEggConfig = (easterEggId: string): EasterEggConfig | undefined => {
  return EASTER_EGG_CONFIGS.find(config => config.id === easterEggId)
}

// Get all easter egg IDs
export const getAllEasterEggIds = (): string[] => {
  return EASTER_EGG_CONFIGS.map(config => config.id)
}

// Get all unlocked colors (for use in color theme generation)
export const getEasterEggColors = () => {
  return EASTER_EGG_CONFIGS.map(config => ({
    ...config.unlockedColor,
    requiredEasterEgg: config.id
  }))
}

// Types for notification handler parameters
export interface NotificationHandlers {
  setShowEasterEggNotification: (message: string | null) => void
  setShowHintNotification: (hint: string | null) => void
  easterEggTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
  hintTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
}

// Create easter egg unlock notification handler
export const createEasterEggNotificationHandler = (handlers: NotificationHandlers) => {
  return (message: string) => {
    const { 
      setShowEasterEggNotification, 
      setShowHintNotification, 
      easterEggTimeoutRef, 
      hintTimeoutRef 
    } = handlers

    // Clear any existing notifications and their timers first
    if (easterEggTimeoutRef.current) {
      clearTimeout(easterEggTimeoutRef.current)
      easterEggTimeoutRef.current = null
    }
    if (hintTimeoutRef.current) {
      clearTimeout(hintTimeoutRef.current)
      hintTimeoutRef.current = null
    }
    
    // Force clear all notifications first
    setShowEasterEggNotification(null)
    setShowHintNotification(null)
    
    // Use a small delay to ensure state is cleared before showing new notification
    setTimeout(() => {
      // Show new easter egg notification
      setShowEasterEggNotification(message)
      
      // Play celebratory arpeggio sound effect
      audioEffects.playAchievementArpeggio()
      
      // Auto-hide after 4 seconds (longer for achievement feel)
      easterEggTimeoutRef.current = setTimeout(() => {
        setShowEasterEggNotification(null)
        easterEggTimeoutRef.current = null
      }, 4000)
    }, 10) // Small delay to ensure state reset
  }
}

// Create hint notification handler
export const createHintNotificationHandler = (handlers: NotificationHandlers) => {
  return (hint: string) => {
    const { 
      setShowEasterEggNotification, 
      setShowHintNotification, 
      easterEggTimeoutRef, 
      hintTimeoutRef 
    } = handlers
    
    // Clear any existing notifications and their timers first
    if (easterEggTimeoutRef.current) {
      clearTimeout(easterEggTimeoutRef.current)
      easterEggTimeoutRef.current = null
    }
    if (hintTimeoutRef.current) {
      clearTimeout(hintTimeoutRef.current)
      hintTimeoutRef.current = null
    }
    
    // Force clear all notifications first
    setShowEasterEggNotification(null)
    setShowHintNotification(null)
    
    // Use a small delay to ensure state is cleared before showing new notification
    setTimeout(() => {
      // Show new hint notification
      setShowHintNotification(hint)
      
      // Auto-hide after 3 seconds
      hintTimeoutRef.current = setTimeout(() => {
        setShowHintNotification(null)
        hintTimeoutRef.current = null
      }, 3000)
    }, 10) // Small delay to ensure state reset
  }
}

/*
CENTRALIZED EASTER EGG MANAGEMENT

This file contains ALL easter egg related functionality:
- Easter egg configurations (colors, names, descriptions)
- Notification handling for unlocks and hints
- Helper functions for easter egg management

HOW TO ADD A NEW EASTER EGG:

1. Add a new object to the EASTER_EGG_CONFIGS array above:
   {
     id: 'my_easter_egg',           // Unique ID (use snake_case)
     name: 'My Easter Egg',          // Display name for achievements
     description: 'How to unlock this', // Hint text for users
     unlockedColor: {
       name: 'MyColor',              // Color theme name
       primary: '#ff0000',           // Primary gradient color
       secondary: '#ff6666'          // Secondary gradient color
     }
   }

2. Implement the unlock logic in your component:
   - Import: unlockEasterEgg from '../utils/easterEggUnlocks'
   - Call: unlockEasterEgg('my_easter_egg') when conditions are met

3. That's it! The color will automatically appear in Settings when unlocked.

NOTIFICATION HANDLERS:
- createEasterEggNotificationHandler: Creates unlock celebration notifications
- createHintNotificationHandler: Creates hint notifications for locked colors
- Both handle proper cleanup and timing to prevent notification conflicts

*/
